import { useEffect } from "react";
import {
  Accordion,
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Fieldset,
  FileButton,
  Group,
  NavLink,
  NumberInput,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ArrowRight, Pencil, RefreshCw, Zap, ZapOff } from "lucide-react";
import { adminErrText, adminGet, adminSend, adminUploadBotAvatar, AdminApiError } from "../api.ts";
import {
  ANALYTICS_NAV,
  type AnalyticsNavSection,
  analyticsSectionOfPath,
  botSectionHref,
  tabToPath,
} from "../lib/bot-nav.ts";
import { ChannelsTab } from "./bot-tabs/ChannelsTab.tsx";
import { CommandsTab } from "./bot-tabs/CommandsTab.tsx";
import { InviteClaimsTab } from "./bot-tabs/InviteClaimsTab.tsx";
import { LanguagesTab } from "./bot-tabs/LanguagesTab.tsx";
import { PrizesTab } from "./bot-tabs/PrizesTab.tsx";
import { TextsTab } from "./bot-tabs/TextsTab.tsx";
import { UsersTab } from "./bot-tabs/UsersTab.tsx";
import { WheelTab } from "./bot-tabs/WheelTab.tsx";
import { PaymentTab } from "./bot-tabs/PaymentTab.tsx";
import { PushTab } from "./bot-tabs/PushTab.tsx";
import { FulfillmentTab } from "./bot-tabs/FulfillmentTab.tsx";
import { StatsTab } from "./bot-tabs/StatsTab.tsx";
import { TargetChatsTab } from "./bot-tabs/analytics/TargetChatsTab.tsx";
import { LandingBgCard, ReportConfigCard } from "./bot-tabs/analytics/AnalyticsBasicConfig.tsx";
import { PlatformCredentialsTab } from "./bot-tabs/analytics/PlatformCredentialsTab.tsx";
import { AdsTab } from "./bot-tabs/analytics/AdsTab.tsx";
import { ConversionsTab } from "./bot-tabs/analytics/ConversionsTab.tsx";
import { LinkPoolTab } from "./bot-tabs/analytics/LinkPoolTab.tsx";
import { ClicksTab } from "./bot-tabs/analytics/ClicksTab.tsx";
import { UntrackedJoinsTab } from "./bot-tabs/analytics/UntrackedJoinsTab.tsx";
import { AnalyticsOverviewTab } from "./bot-tabs/analytics/AnalyticsOverviewTab.tsx";
import { ChatFunnelTab } from "./bot-tabs/analytics/ChatFunnelTab.tsx";
import { MemberReconcileTab } from "./bot-tabs/analytics/MemberReconcileTab.tsx";
import { AdsGeoTab } from "./bot-tabs/analytics/AdsGeoTab.tsx";
import { ShortlinkDiagnosisTab } from "./bot-tabs/analytics/ShortlinkDiagnosisTab.tsx";
import type { UntrackedJoinsNavFilters } from "./bot-tabs/analytics/untracked-join-shared.ts";
import type { AnalyticsUntrackedJoin, PaginatedResponse } from "../api.ts";

interface BotDetail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  botUsername: string | null;
  isEnabled: boolean;
  onboardingSource: "manual" | "managed";
  currency: string;
  defaultLanguage: string;
  languages: string[];
  timezone: string;
  miniAppUrl: string;
  miniAppShortName: string | null;
  inviteSiteUrl: string | null;
  rewardRules: {
    invite: { enabled: boolean; spins: number; minDeposit: number };
    checkin: { enabled: boolean; spins: number; url: string };
    deposit: {
      enabled: boolean;
      baseSpins: number;
      threshold: number;
      bonusSpins: number;
      url: string;
    };
  } | null;
  dauEstimate: number | null;
  mode: string;
  botType: "lottery" | "analytics";
  lastRuntimeError: string | null;
}

interface BotEditForm {
  name: string;
  description: string;
  currency: string;
  timezone: string;
  miniAppUrl: string;
  miniAppShortName: string;
  inviteSiteUrl: string;
  dauEstimate: number | string;
  inviteEnabled: boolean;
  inviteSpins: number | string;
  inviteMinDeposit: number | string;
  checkinEnabled: boolean;
  checkinSpins: number | string;
  depositEnabled: boolean;
  depositBaseSpins: number | string;
  depositThreshold: number | string;
  depositBonusSpins: number | string;
}

const DEFAULT_REWARD_RULES = {
  invite: { enabled: true, spins: 1, minDeposit: 0 },
  checkin: { enabled: true, spins: 1, url: "" },
  deposit: { enabled: true, baseSpins: 1, threshold: 500, bonusSpins: 1, url: "" },
};

// URL 格式校验：required 时空值报"必填"，否则空值放过（仅当非空时校验）。
// 强制 https + 合法主机，避免运营粘贴 http://example.com 这种死链。
const validateHttpsUrl = (v: string, required: boolean): string | null => {
  const s = v.trim();
  if (s.length === 0) return required ? "必填" : null;
  let parsed: URL;
  try {
    parsed = new URL(s);
  } catch {
    return "URL 格式无效";
  }
  if (parsed.protocol !== "https:") return "必须 https://";
  if (parsed.hostname.length === 0 || !parsed.hostname.includes(".")) {
    return "主机名无效";
  }
  return null;
};

interface Readiness {
  ready: boolean;
  missing: ReadonlyArray<string>;
  identity: { id: number; username: string } | null;
}

const MISSING_ZH: Record<string, string> = {
  wheel_config_invalid: "转盘配置（wheel_config）格式不合法",
  wheel_sector_short: "转盘扇区颜色数量小于已启用奖品数",
  wheel_weights_zero: "已启用奖品的总权重为 0",
  no_enabled_prize: "至少需要 1 个启用的奖品",
  bot_token_decrypt: "Bot token 解密失败（加密 key 错？）",
  bot_unreachable: "Telegram getMe 调用失败（token 失效或网络异常）",
  bot_identity_mismatch: "Telegram getMe 返回的 username/id 与库内不一致",
  no_chat: "至少需要 1 个已启用的频道/群聊",
  bot_not_admin: "Bot 尚未成为频道/群聊的管理员（需 can_invite_users 权限）",
  no_credential: "至少需要 1 个已启用的平台凭据（TikTok/Facebook）",
  pool_empty: "链接池未预热，请等待 cron 补池或手动触发",
};

const renderMissing = (key: string): string => {
  if (key.startsWith("text:")) {
    return `缺少文案：${key.slice(5)}`;
  }
  return MISSING_ZH[key] ?? key;
};

const missingToTab = (key: string): string | null => {
  if (
    key === "wheel_config_invalid" ||
    key === "wheel_sector_short" ||
    key === "wheel_weights_zero"
  )
    return "wheel";
  if (key === "no_enabled_prize") return "prizes";
  if (key.startsWith("text:")) return "texts";
  if (key === "no_chat" || key === "bot_not_admin") return "chats";
  if (key === "no_credential") return "credentials";
  if (key === "pool_empty") return "pool";
  return null;
};

interface MissingItemProps {
  readonly code: string;
  readonly onNavigate: (tab: string) => void;
  readonly onTestToken: () => void;
}

function MissingItem(props: MissingItemProps) {
  const { code, onNavigate, onTestToken } = props;
  const tab = missingToTab(code);
  const isTokenIssue =
    code === "bot_unreachable" || code === "bot_token_decrypt" || code === "bot_identity_mismatch";

  return (
    <li style={{ marginBottom: 4 }}>
      <Group gap="xs" wrap="nowrap">
        <Text size="sm" style={{ flex: 1 }}>
          {renderMissing(code)}
        </Text>
        {tab !== null ? (
          <Button
            size="xs"
            variant="light"
            rightSection={<ArrowRight size={12} />}
            onClick={() => onNavigate(tab)}
          >
            去配置
          </Button>
        ) : isTokenIssue ? (
          <Button size="xs" variant="light" onClick={onTestToken}>
            测试 Token
          </Button>
        ) : null}
      </Group>
    </li>
  );
}

export function BotDetailPage() {
  const { id = "", "*": splat = "" } = useParams<{ id: string; "*": string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();

  // 子路径：/bots/:id/credentials/sharechat -> section=credentials, sub=sharechat
  const pathSegments = splat.split("/").filter(Boolean);
  const sectionPath = pathSegments[0] ?? "";

  // 兼容旧链 ?tab=analytics-credentials -> /bots/:id/credentials；空路径 -> basic
  // 分析 bot 旧「报表」/stats -> overview
  const legacyTab = searchParams.get("tab");
  useEffect(() => {
    if (legacyTab !== null && legacyTab !== "") {
      const path = tabToPath(legacyTab);
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      const q = next.toString();
      void navigate(`/bots/${id}/${path}${q !== "" ? `?${q}` : ""}`, { replace: true });
      return;
    }
    if (sectionPath === "") {
      void navigate(`/bots/${id}/basic`, { replace: true });
    }
  }, [id, legacyTab, sectionPath, navigate, searchParams]);

  const activeTab = sectionPath === "" ? "basic" : sectionPath;

  /** 仍接受旧 tab key 或新 path，统一 navigate 到子路由 */
  const setActiveTab = (v: string): void => {
    const path = tabToPath(v);
    void navigate(botSectionHref(id, path), { replace: true });
  };

  const openVisits = (filters?: {
    readonly disposition?: string;
    readonly chatId?: string;
  }): void => {
    const next = new URLSearchParams();
    if (filters?.disposition !== undefined && filters.disposition !== "") {
      next.set("visitDisposition", filters.disposition);
    }
    if (filters?.chatId !== undefined && filters.chatId !== "") {
      next.set("visitChatId", filters.chatId);
    }
    const q = next.toString();
    void navigate(`/bots/${id}/clicks${q !== "" ? `?${q}` : ""}`, { replace: true });
  };

  const openUntrackedJoins = (filters?: UntrackedJoinsNavFilters): void => {
    const next = new URLSearchParams();
    if (filters?.chatId !== undefined && filters.chatId !== "") {
      next.set("chatId", filters.chatId);
    }
    if (filters?.joinSource !== undefined) {
      next.set("joinSource", filters.joinSource);
    }
    if (filters?.inviteLinkKey !== undefined && filters.inviteLinkKey.trim() !== "") {
      next.set("inviteLinkKey", filters.inviteLinkKey.trim());
    }
    const q = next.toString();
    void navigate(`/bots/${id}/untracked-joins${q !== "" ? `?${q}` : ""}`, { replace: true });
  };

  const { data: bot } = useQuery({
    queryKey: ["bot", id],
    queryFn: () => adminGet<BotDetail>(`/bots/${id}`),
  });

  // 分析 bot 旧路径 /stats 整页报表 -> 概览
  useEffect(() => {
    if (bot?.botType === "analytics" && sectionPath === "stats") {
      void navigate(`/bots/${id}/overview`, { replace: true });
    }
  }, [bot?.botType, sectionPath, id, navigate]);

  // 仅探测是否有未归因记录（keyset 无精确 total，避免 COUNT 全表）
  const { data: hasUntrackedJoins } = useQuery({
    queryKey: ["bot", id, "analytics-untracked-joins-presence"],
    queryFn: () =>
      adminGet<PaginatedResponse<AnalyticsUntrackedJoin>>(
        `/analytics/${id}/untracked-joins?pageSize=1`,
      ),
    select: (d) => d.items.length > 0 || d.hasMore === true,
    enabled: bot?.botType === "analytics",
    staleTime: 60_000,
  });

  const { data: readiness, refetch: refetchReadiness } = useQuery({
    queryKey: ["bot", id, "readiness"],
    queryFn: () => adminGet<Readiness>(`/bots/${id}/readiness`),
  });

  const reload = useMutation({
    mutationFn: () => adminSend("POST", `/bots/${id}/reload`),
    onSuccess: () => notifications.show({ color: "green", message: "已触发热加载" }),
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const testToken = useMutation({
    mutationFn: () => adminSend<{ username: string }>("POST", `/bots/${id}/test-token`),
    onSuccess: (r) => notifications.show({ color: "green", message: `Token OK：@${r.username}` }),
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const toggleEnabled = useMutation({
    mutationFn: (isEnabled: boolean) => adminSend("PATCH", `/bots/${id}`, { isEnabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bot", id] });
      void qc.invalidateQueries({ queryKey: ["bots"] });
      notifications.show({ color: "green", message: "已更新启用状态" });
    },
    onError: (e) => {
      const err = e as AdminApiError & { details?: { missing?: string[] } };
      if (err.code === "BOT_NOT_READY") {
        const missing = err.details?.missing ?? [];
        notifications.show({
          color: "red",
          message: `Bot 配置未完成，无法启用：${missing.map(renderMissing).join("、")}`,
        });
        void refetchReadiness();
      } else {
        notifications.show({ color: "red", message: adminErrText(err.code, err.message) });
      }
    },
  });

  const editForm = useForm<BotEditForm>({
    initialValues: {
      name: "",
      description: "",
      currency: "",
      timezone: "",
      miniAppUrl: "",
      miniAppShortName: "",
      inviteSiteUrl: "",
      dauEstimate: "",
      inviteEnabled: true,
      inviteSpins: 1,
      inviteMinDeposit: 0,
      checkinEnabled: true,
      checkinSpins: 1,
      depositEnabled: true,
      depositBaseSpins: 1,
      depositThreshold: 500,
      depositBonusSpins: 1,
    },
    validate: {
      name: (v) => (v.trim().length === 0 ? "必填" : null),
      miniAppUrl: (v) => validateHttpsUrl(v, true),
      inviteSiteUrl: (v) => validateHttpsUrl(v, false),
      inviteSpins: (v) => (Number(v) < 0 || !Number.isInteger(Number(v)) ? "必须为非负整数" : null),
      inviteMinDeposit: (v) => (Number(v) < 0 ? "必须为非负数" : null),
      checkinSpins: (v) =>
        Number(v) < 0 || !Number.isInteger(Number(v)) ? "必须为非负整数" : null,
      depositBaseSpins: (v) =>
        Number(v) < 0 || !Number.isInteger(Number(v)) ? "必须为非负整数" : null,
      depositThreshold: (v) => (Number(v) <= 0 ? "必须大于 0" : null),
      depositBonusSpins: (v) =>
        Number(v) < 0 || !Number.isInteger(Number(v)) ? "必须为非负整数" : null,
    },
  });

  // bot 数据加载后用当前配置填充「基础设置」tab 表单（编辑表单常驻 tab，非弹窗）。
  useEffect(() => {
    if (bot === undefined) return;
    const rr = bot.rewardRules ?? DEFAULT_REWARD_RULES;
    editForm.setValues({
      name: bot.name,
      description: bot.description ?? "",
      currency: bot.currency,
      timezone: bot.timezone,
      miniAppUrl: bot.miniAppUrl,
      miniAppShortName: bot.miniAppShortName ?? "",
      inviteSiteUrl: bot.inviteSiteUrl ?? "",
      dauEstimate: bot.dauEstimate ?? "",
      inviteEnabled: rr.invite.enabled,
      inviteSpins: rr.invite.spins,
      inviteMinDeposit: rr.invite.minDeposit,
      checkinEnabled: rr.checkin.enabled,
      checkinSpins: rr.checkin.spins,
      depositEnabled: rr.deposit.enabled,
      depositBaseSpins: rr.deposit.baseSpins,
      depositThreshold: rr.deposit.threshold,
      depositBonusSpins: rr.deposit.bonusSpins,
    });
  }, [bot]);

  const patchBot = useMutation({
    mutationFn: (v: BotEditForm) =>
      adminSend("PATCH", `/bots/${id}`, {
        name: v.name.trim(),
        description: v.description.trim() === "" ? null : v.description.trim(),
        currency: v.currency.trim(),
        timezone: v.timezone.trim(),
        miniAppUrl: v.miniAppUrl.trim(),
        miniAppShortName: v.miniAppShortName.trim() === "" ? null : v.miniAppShortName.trim(),
        inviteSiteUrl: v.inviteSiteUrl.trim() === "" ? null : v.inviteSiteUrl.trim(),
        dauEstimate: v.dauEstimate === "" ? null : Number(v.dauEstimate),
        rewardRules: {
          invite: {
            enabled: v.inviteEnabled,
            spins: Number(v.inviteSpins),
            minDeposit: Number(v.inviteMinDeposit),
          },
          checkin: {
            enabled: v.checkinEnabled,
            spins: Number(v.checkinSpins),
            // 字段保留兼容 schema；前端统一用 bot.invite_site_url，admin 不再可配子页 URL。
            url: "",
          },
          deposit: {
            enabled: v.depositEnabled,
            baseSpins: Number(v.depositBaseSpins),
            threshold: Number(v.depositThreshold),
            bonusSpins: Number(v.depositBonusSpins),
            url: "",
          },
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bot", id] });
      void qc.invalidateQueries({ queryKey: ["bots"] });
      notifications.show({ color: "green", message: "已保存基本信息" });
    },
    onError: (e) =>
      notifications.show({
        color: "red",
        message: adminErrText((e as AdminApiError).code, (e as AdminApiError).message),
      }),
  });

  // Telegram 头像：setMyProfilePhoto 设置 / removeMyProfilePhoto 移除。仅 JPG。
  const setAvatar = useMutation({
    mutationFn: (file: File) => adminUploadBotAvatar(id, file),
    onSuccess: () => notifications.show({ color: "green", message: "已更新 Telegram 头像" }),
    onError: (e) =>
      notifications.show({
        color: "red",
        message: adminErrText((e as AdminApiError).code, (e as AdminApiError).message),
      }),
  });
  const removeAvatar = useMutation({
    mutationFn: () => adminSend("DELETE", `/bots/${id}/avatar`),
    onSuccess: () => notifications.show({ color: "green", message: "已移除 Telegram 头像" }),
    onError: (e) =>
      notifications.show({
        color: "red",
        message: adminErrText((e as AdminApiError).code, (e as AdminApiError).message),
      }),
  });

  const switchBotType = useMutation({
    mutationFn: (botType: "lottery" | "analytics") =>
      adminSend("PATCH", `/bots/${id}`, { botType }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["bot", id] });
      notifications.show({ color: "green", message: "已切换类型" });
    },
    onError: (e) =>
      notifications.show({
        color: "red",
        message: adminErrText((e as AdminApiError).code, (e as AdminApiError).message),
      }),
  });

  const canEnable = readiness?.ready === true;
  const botTimezone = bot?.timezone ?? "Asia/Kolkata";
  const avatarLetter = (bot?.botUsername ?? bot?.name ?? "?").slice(0, 1).toUpperCase();

  // readiness accordion: open by default when not ready
  const readinessDefaultValue = readiness !== undefined && !readiness.ready ? "readiness" : null;

  return (
    <Stack gap="md">
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          {/* 左侧：Avatar + 名称 + badges */}
          <Group gap="sm" align="flex-start">
            <Avatar size="lg" color="blue" radius="md">
              {avatarLetter}
            </Avatar>
            <Stack gap={4}>
              <Group gap={6} wrap="nowrap">
                <Text fw={700} size="xl" lh={1.2}>
                  {bot?.name ?? "-"}
                </Text>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => setActiveTab("basic")}
                  disabled={bot === undefined}
                  title="基础设置"
                >
                  <Pencil size={14} />
                </ActionIcon>
              </Group>
              <Group gap="xs">
                <Badge color="gray" variant="outline" size="sm">
                  {bot?.code}
                </Badge>
                {bot?.onboardingSource === "managed" ? (
                  <Badge color="violet" size="sm">
                    Managed
                  </Badge>
                ) : (
                  <Badge color="gray" size="sm">
                    Manual
                  </Badge>
                )}
                {bot?.mode !== undefined && bot.mode !== "" ? (
                  <Badge color="teal" size="sm">
                    {bot.mode}
                  </Badge>
                ) : null}
                {bot?.botUsername !== null && bot?.botUsername !== undefined ? (
                  <Badge color="blue" size="sm">
                    @{bot.botUsername}
                  </Badge>
                ) : null}
                {bot?.botType === "analytics" ? (
                  <Badge color="violet" size="sm">
                    分析机器人
                  </Badge>
                ) : bot !== undefined ? (
                  <Badge color="teal" size="sm">
                    转盘机器人
                  </Badge>
                ) : null}
              </Group>
              <Group gap="xs" mt={2}>
                <FileButton
                  onChange={(f) => {
                    if (f !== null) setAvatar.mutate(f);
                  }}
                  accept="image/jpeg"
                >
                  {(props) => (
                    <Button
                      {...props}
                      size="xs"
                      variant="light"
                      loading={setAvatar.isPending}
                      disabled={bot === undefined}
                    >
                      设置 TG 头像
                    </Button>
                  )}
                </FileButton>
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  loading={removeAvatar.isPending}
                  disabled={bot === undefined}
                  onClick={() => removeAvatar.mutate()}
                >
                  移除
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  color="orange"
                  loading={switchBotType.isPending}
                  disabled={bot === undefined}
                  onClick={() => {
                    const targetType = bot?.botType === "analytics" ? "lottery" : "analytics";
                    const targetLabel = targetType === "analytics" ? "分析机器人" : "转盘机器人";
                    modals.openConfirmModal({
                      title: "确认切换机器人类型",
                      children: (
                        <Text size="sm">
                          确定要将 Bot「{bot?.name}」切换为{targetLabel}吗？
                          <br />
                          转盘数据与分析数据各自保留，切回即用，不会丢失。
                          {targetType === "analytics" ? (
                            <>
                              <br />
                              切换为分析机器人后，需在「频道/群聊 / 平台凭据 /
                              广告列表」完成配置后才能启用。
                            </>
                          ) : null}
                        </Text>
                      ),
                      labels: { confirm: "确认切换", cancel: "取消" },
                      confirmProps: { color: "orange" },
                      onConfirm: () => switchBotType.mutate(targetType),
                    });
                  }}
                >
                  {bot?.botType === "analytics" ? "切换为转盘机器人" : "切换为分析机器人"}
                </Button>
              </Group>
            </Stack>
          </Group>

          {/* 右侧：Switch + 操作按钮组 */}
          <Stack gap="xs" align="flex-end">
            <Switch
              checked={bot?.isEnabled === true}
              disabled={bot === undefined || (!bot.isEnabled && !canEnable)}
              onChange={(e) => toggleEnabled.mutate(e.currentTarget.checked)}
              onLabel="启用"
              offLabel="停用"
              size="md"
            />
            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                size="md"
                onClick={() => reload.mutate()}
                loading={reload.isPending}
                title="立即热加载"
              >
                <Zap size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                size="md"
                onClick={() => testToken.mutate()}
                loading={testToken.isPending}
                title="测试 Token"
              >
                <ZapOff size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                size="md"
                onClick={() => void refetchReadiness()}
                title="刷新启用预检"
              >
                <RefreshCw size={16} />
              </ActionIcon>
            </Group>
            <Group gap="xs">
              <Button
                variant="outline"
                size="xs"
                onClick={() => reload.mutate()}
                loading={reload.isPending}
              >
                热加载
              </Button>
              <Button
                variant="outline"
                size="xs"
                onClick={() => testToken.mutate()}
                loading={testToken.isPending}
              >
                测试 Token
              </Button>
              <Button variant="outline" size="xs" onClick={() => void refetchReadiness()}>
                刷新预检
              </Button>
            </Group>
          </Stack>
        </Group>

        {bot?.lastRuntimeError !== null && bot?.lastRuntimeError !== undefined ? (
          <Alert color="red" title="运行时错误" mt="md">
            {bot.lastRuntimeError}
          </Alert>
        ) : null}

        {readiness !== undefined ? (
          <Accordion mt="md" defaultValue={readinessDefaultValue} variant="contained">
            <Accordion.Item value="readiness">
              <Accordion.Control>
                {readiness.ready ? (
                  <Group gap="xs">
                    <Badge color="green" size="sm">
                      预检通过
                    </Badge>
                    <Text size="sm" c="dimmed">
                      {readiness.identity != null
                        ? `Telegram 身份：@${readiness.identity.username} (id=${readiness.identity.id})`
                        : "配置完整，可启用。"}
                    </Text>
                  </Group>
                ) : (
                  <Group gap="xs">
                    <Badge color="red" size="sm">
                      预检未通过
                    </Badge>
                    <Text size="sm" c="dimmed">
                      {readiness.missing.length} 项待修复，无法启用
                    </Text>
                  </Group>
                )}
              </Accordion.Control>
              <Accordion.Panel>
                {readiness.ready ? (
                  <Alert color="green">
                    {readiness.identity != null
                      ? `已确认 Telegram 身份：@${readiness.identity.username} (id=${readiness.identity.id})`
                      : "配置完整。"}
                  </Alert>
                ) : (
                  <Alert color="yellow" title="以下配置缺失，无法启用">
                    <ul style={{ margin: 0, paddingLeft: 20, listStyle: "none" }}>
                      {readiness.missing.map((m) => (
                        <MissingItem
                          key={m}
                          code={m}
                          onNavigate={(tab) => setActiveTab(tab)}
                          onTestToken={() => testToken.mutate()}
                        />
                      ))}
                    </ul>
                  </Alert>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        ) : null}
      </Card>

      {bot?.botType === "analytics" ? (
        <SegmentedControl
          mb="sm"
          value={analyticsSectionOfPath(activeTab)}
          onChange={(v) => {
            const section = v as AnalyticsNavSection;
            const items = ANALYTICS_NAV[section];
            const first = items[0];
            if (first === undefined) return;
            if (!items.some((x) => x.path === activeTab)) {
              setActiveTab(first.path);
            }
          }}
          data={[
            { label: "配置", value: "setup" },
            { label: "数据", value: "data" },
            { label: "诊断", value: "diagnose" },
          ]}
        />
      ) : null}

      <Group align="flex-start" gap="md" wrap="nowrap">
        {bot?.botType === "analytics" ? (
          <Stack gap={4} style={{ width: 168, flexShrink: 0 }}>
            {ANALYTICS_NAV[analyticsSectionOfPath(activeTab)].map((item) => (
              <NavLink
                key={item.path}
                component={Link}
                to={botSectionHref(id, item.path)}
                label={
                  item.path === "untracked-joins" && hasUntrackedJoins === true ? (
                    <Group gap={6} wrap="nowrap">
                      {item.label}
                      <Badge size="sm" variant="filled" color="orange">
                        有
                      </Badge>
                    </Group>
                  ) : (
                    item.label
                  )
                }
                active={activeTab === item.path}
                variant="light"
              />
            ))}
          </Stack>
        ) : null}

        <div style={{ flex: 1, minWidth: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(v) => setActiveTab(v ?? "basic")}
            variant="pills"
            radius="md"
          >
            {bot?.botType !== "analytics" ? (
              <Tabs.List>
                <Tabs.Tab value="basic">基础设置</Tabs.Tab>
                <Tabs.Tab value="wheel">转盘</Tabs.Tab>
                <Tabs.Tab value="languages">语言</Tabs.Tab>
                <Tabs.Tab value="prizes">奖品池</Tabs.Tab>
                <Tabs.Tab value="commands">命令</Tabs.Tab>
                <Tabs.Tab value="push">推送</Tabs.Tab>
                <Tabs.Tab value="invite-claims">邀请申报</Tabs.Tab>
                <Tabs.Tab value="fulfillment">履约</Tabs.Tab>
                <Tabs.Tab value="payment">支付</Tabs.Tab>
                <Tabs.Tab value="channels">频道</Tabs.Tab>
                <Tabs.Tab value="texts">文案</Tabs.Tab>
                <Tabs.Tab value="users">用户</Tabs.Tab>
                <Tabs.Tab value="stats">报表</Tabs.Tab>
              </Tabs.List>
            ) : (
              <Tabs.List style={{ display: "none" }}>
                <Tabs.Tab value="basic">基础设置</Tabs.Tab>
                <Tabs.Tab value="chats">频道/群聊</Tabs.Tab>
                <Tabs.Tab value="credentials">平台凭据</Tabs.Tab>
                <Tabs.Tab value="ads">广告列表</Tabs.Tab>
                <Tabs.Tab value="pool">链接池</Tabs.Tab>
                <Tabs.Tab value="conversions">转化记录</Tabs.Tab>
                <Tabs.Tab value="clicks">访问记录</Tabs.Tab>
                <Tabs.Tab value="overview">概览</Tabs.Tab>
                <Tabs.Tab value="chat-funnel">群组漏斗</Tabs.Tab>
                <Tabs.Tab value="member-reconcile">人数对账</Tabs.Tab>
                <Tabs.Tab value="ads-geo">广告与地域</Tabs.Tab>
                <Tabs.Tab value="diagnosis">链接诊断</Tabs.Tab>
                <Tabs.Tab value="untracked-joins">未归因入群</Tabs.Tab>
              </Tabs.List>
            )}
            <Tabs.Panel value="basic" pt="md">
              <Stack gap="md">
                <form onSubmit={editForm.onSubmit((v) => patchBot.mutate(v))}>
                  <Stack gap="md">
                    <Fieldset legend="基本信息">
                      <Stack gap="sm">
                        <SimpleGrid cols={2}>
                          <TextInput label="名称" required {...editForm.getInputProps("name")} />
                          <TextInput
                            label="货币（如 INR）"
                            {...editForm.getInputProps("currency")}
                          />
                          <TextInput
                            label="统计时区"
                            description="印度市场固定按印度标准时间 (IST) 切日；请勿因运营人员所在地修改"
                            readOnly
                            {...editForm.getInputProps("timezone")}
                          />
                        </SimpleGrid>
                        <Textarea
                          label="描述（可选）"
                          rows={2}
                          {...editForm.getInputProps("description")}
                        />
                      </Stack>
                    </Fieldset>
                    {bot?.botType !== "analytics" ? (
                      <>
                        <Fieldset legend="Mini App 与运营参数">
                          <SimpleGrid cols={2}>
                            <TextInput
                              label="Mini App URL"
                              required
                              {...editForm.getInputProps("miniAppUrl")}
                            />
                            <TextInput
                              label="Mini App short_name（可选）"
                              {...editForm.getInputProps("miniAppShortName")}
                            />
                            <TextInput
                              label="拉新注册站点链接（可选）"
                              {...editForm.getInputProps("inviteSiteUrl")}
                            />
                            <NumberInput
                              label="DAU 预估（可选）"
                              min={0}
                              {...editForm.getInputProps("dauEstimate")}
                            />
                          </SimpleGrid>
                        </Fieldset>
                        <Fieldset legend="奖励规则">
                          <Stack gap="sm">
                            <Fieldset legend="邀请奖励">
                              <SimpleGrid cols={2}>
                                <Switch
                                  label="启用邀请奖励"
                                  {...editForm.getInputProps("inviteEnabled", {
                                    type: "checkbox",
                                  })}
                                />
                                <NumberInput
                                  label="邀请券数"
                                  min={0}
                                  allowDecimal={false}
                                  {...editForm.getInputProps("inviteSpins")}
                                />
                                <NumberInput
                                  label="下级首充最低金额（0=不限）"
                                  min={0}
                                  {...editForm.getInputProps("inviteMinDeposit")}
                                />
                              </SimpleGrid>
                            </Fieldset>
                            <Fieldset legend="签到奖励">
                              <SimpleGrid cols={2}>
                                <Switch
                                  label="启用签到奖励"
                                  {...editForm.getInputProps("checkinEnabled", {
                                    type: "checkbox",
                                  })}
                                />
                                <NumberInput
                                  label="签到券数"
                                  min={0}
                                  allowDecimal={false}
                                  {...editForm.getInputProps("checkinSpins")}
                                />
                              </SimpleGrid>
                            </Fieldset>
                            <Fieldset legend="充值奖励">
                              <SimpleGrid cols={2}>
                                <Switch
                                  label="启用充值奖励"
                                  {...editForm.getInputProps("depositEnabled", {
                                    type: "checkbox",
                                  })}
                                />
                                <NumberInput
                                  label="基础券数"
                                  min={0}
                                  allowDecimal={false}
                                  {...editForm.getInputProps("depositBaseSpins")}
                                />
                                <NumberInput
                                  label="额外赠送金额阈值"
                                  min={1}
                                  {...editForm.getInputProps("depositThreshold")}
                                />
                                <NumberInput
                                  label="满额额外赠券数"
                                  min={0}
                                  allowDecimal={false}
                                  {...editForm.getInputProps("depositBonusSpins")}
                                />
                              </SimpleGrid>
                            </Fieldset>
                          </Stack>
                        </Fieldset>
                      </>
                    ) : null}
                    <Group justify="flex-end">
                      <Button type="submit" loading={patchBot.isPending}>
                        保存
                      </Button>
                    </Group>
                  </Stack>
                </form>
                {bot?.botType === "analytics" ? (
                  <>
                    <ReportConfigCard botId={id} />
                    <LandingBgCard botId={id} />
                  </>
                ) : null}
              </Stack>
            </Tabs.Panel>
            {bot?.botType !== "analytics" ? (
              <>
                <Tabs.Panel value="wheel" pt="md">
                  <WheelTab botId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="languages" pt="md">
                  <LanguagesTab
                    botId={id}
                    languages={bot?.languages ?? ["en-IN"]}
                    defaultLanguage={bot?.defaultLanguage ?? "en-IN"}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="prizes" pt="md">
                  <PrizesTab botId={id} languages={bot?.languages ?? ["en-IN"]} />
                </Tabs.Panel>
                <Tabs.Panel value="commands" pt="md">
                  <CommandsTab botId={id} languages={bot?.languages ?? ["en-IN"]} />
                </Tabs.Panel>
                <Tabs.Panel value="push" pt="md">
                  <PushTab botId={id} languages={bot?.languages ?? ["en-IN"]} />
                </Tabs.Panel>
                <Tabs.Panel value="invite-claims" pt="md">
                  <InviteClaimsTab botId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="fulfillment" pt="md">
                  <FulfillmentTab botId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="payment" pt="md">
                  <PaymentTab botId={id} />
                </Tabs.Panel>
              </>
            ) : null}
            {bot?.botType !== "analytics" ? (
              <>
                <Tabs.Panel value="channels" pt="md">
                  <ChannelsTab botId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="texts" pt="md">
                  <TextsTab botId={id} languages={bot?.languages ?? ["en-IN"]} />
                </Tabs.Panel>
                <Tabs.Panel value="users" pt="md">
                  <UsersTab botId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="stats" pt="md">
                  <StatsTab botId={id} />
                </Tabs.Panel>
              </>
            ) : null}
            {bot?.botType === "analytics" ? (
              <>
                <Tabs.Panel value="chats" pt="md">
                  <TargetChatsTab botId={id} timezone={botTimezone} />
                </Tabs.Panel>
                <Tabs.Panel value="credentials" pt="md">
                  <PlatformCredentialsTab botId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="ads" pt="md">
                  <AdsTab botId={id} timezone={botTimezone} />
                </Tabs.Panel>
                <Tabs.Panel value="conversions" pt="md">
                  <ConversionsTab botId={id} timezone={botTimezone} />
                </Tabs.Panel>
                <Tabs.Panel value="pool" pt="md">
                  <LinkPoolTab botId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="diagnosis" pt="md">
                  <ShortlinkDiagnosisTab botId={id} onOpenVisits={openVisits} />
                </Tabs.Panel>
                <Tabs.Panel value="clicks" pt="md">
                  <ClicksTab botId={id} timezone={botTimezone} />
                </Tabs.Panel>
                <Tabs.Panel value="untracked-joins" pt="md">
                  <UntrackedJoinsTab
                    botId={id}
                    timezone={botTimezone}
                    onOpenStats={() => setActiveTab("overview")}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="overview" pt="md">
                  <AnalyticsOverviewTab
                    botId={id}
                    onOpenUntrackedJoins={openUntrackedJoins}
                    onOpenTab={(tab) => setActiveTab(tab)}
                  />
                </Tabs.Panel>
                <Tabs.Panel value="chat-funnel" pt="md">
                  <ChatFunnelTab botId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="member-reconcile" pt="md">
                  <MemberReconcileTab botId={id} onOpenUntrackedJoins={openUntrackedJoins} />
                </Tabs.Panel>
                <Tabs.Panel value="ads-geo" pt="md">
                  <AdsGeoTab botId={id} />
                </Tabs.Panel>
              </>
            ) : null}
          </Tabs>
        </div>
      </Group>
    </Stack>
  );
}
